import {
  createRouter,
  createWebHashHistory,
  createWebHistory,
  type LocationQuery,
  type RouteLocationRaw,
} from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useSocketStore } from '../stores/socket';
import { isLocal, isCloud, isElectron, isCapacitor, readRuntimeFlavor, readTabActiveHub } from '../config/runtime';
import { resolveHubOpenPlan } from '../utils/hubOpenFallback';
import { classifyCloudSessionRefusal } from '../services/cloudSessionRefusal';
import { hydrateServerProfiles, profileFor } from '../services/serverProfiles';
import { knownHubAddress } from '../services/localServerTable';
import { errorCode, errorStatus } from '../utils/errors';
import { isPanelDeviceTakeover } from '../config/panelDevice';
import { useWebRTCStore } from '../stores/webrtc';
import {
  LAST_HUB_PATH,
  LAST_VIEWED_DASHBOARD,
  LAST_VIEWED_ROOMS_PAGE,
  LAST_VIEWED_SETTINGS_PAGE,
  readHubPosition,
  resolveRoomsPosition,
  visitedConnection,
  writeHubPosition,
  isMissingRoom,
} from '../utils/hubPositions';
import { isLayoutDashboard } from '../utils/layoutMarker';
import { isSystemPageRow } from '../utils/systemPages';
import { pickMobileDefault } from '../utils/mobileDashboard';
import { resolveNarrowSurface } from '../utils/dashboardLayoutMode';

const useHashHistory = isCapacitor() || isElectron();

/**
 * Does the hub being VISITED still have this room? Only ever used to validate a
 * stored room id, so it costs one request on the rooms entry route. A hub that
 * does not have the room answers 404 (`false`); anything else the request can
 * do — offline, refused, unauthenticated — is reported as "could not tell"
 * (`null`) so the stored position is kept rather than silently dropped.
 */
async function roomExistsOnVisitedHub(roomId: string): Promise<boolean | null> {
  try {
    const { apiFetch } = await import('../services/api');
    await apiFetch(`/rooms/${roomId}`);
    return true;
  } catch (error) {
    return isMissingRoom(error) ? false : null;
  }
}

function getRouteParam(value: unknown): string {
  return Array.isArray(value) ? String(value[0] ?? '') : String(value ?? '');
}

function redirectToSettingsIntegration(
  to: { params: Record<string, unknown>; query: LocationQuery; hash: string },
  segment: string,
): RouteLocationRaw {
  const hubId = to.params.hubId ? `/h/${getRouteParam(to.params.hubId)}` : '';
  return {
    path: `${hubId}/settings/integrations/${getRouteParam(to.params.id)}/${segment}`,
    query: to.query,
    hash: to.hash,
  };
}

const router = createRouter({
  history: useHashHistory ? createWebHashHistory() : createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    // Preserve scroll position on back navigation
    if (savedPosition) {
      return savedPosition;
    }
    // Scroll to top for new routes
    return { top: 0 };
  },
  routes: [
    {
      // Touch panel device pairing flow (replaces login on dedicated tablet/panel devices)
      path: '/pair',
      name: 'PanelPairing',
      component: () => import('../views/PanelPairing.vue'),
      meta: { requiresAuth: false, panelPairing: true },
    },
    {
      // Play device pairing flow
      path: '/play-pair',
      name: 'PlayPairing',
      component: () => import('../views/PlayPairing.vue'),
      meta: { requiresAuth: false, panelPairing: true },
    },
    {
      // Boot route for Electron shell tabs (electron/tabs.ts): optionally
      // connects to the tab's server with saved credentials, then navigates
      // to the `route` query target.
      path: '/tab-connect',
      name: 'TabConnect',
      component: () => import('../views/TabConnect.vue'),
      meta: { requiresAuth: false },
    },
    {
      // Electron launcher — the pinned home tab: connected session, built-in
      // apps, saved/cloud servers and local discovery. Browser builds are
      // redirected to /servers by the guard below.
      path: '/home',
      name: 'Home',
      component: () => import('../views/ServersLauncher.vue'),
      meta: { requiresAuth: false, bare: true },
    },
    {
      // Unified connection screen — replaces /connect and /cloud.
      // Legacy URL: the standalone Servers page is gone — the dashboard
      // (`/home`) carries the whole server section on every platform. Kept as a
      // redirect so old links, QR codes and guard fallbacks keep working.
      path: '/servers',
      name: 'Servers',
      redirect: '/home',
    },
    {
      // Find local servers — the hubs on this network that are not in your
      // servers yet. The surface is a **modal** over the launcher home (owner's
      // change 2026-09-30, plan `HOME-ONLY-SERVER-CARDS.md` §3 C0–C1), not a
      // page: the route is kept so old links, the shell, `GlobalSearch`,
      // `HubNeedsCredential` and every probe that opens `#/local` keep working,
      // and it lands on the home with `?find=1` — the marker
      // `components/server/ServersSection.vue` reads to open the modal. The
      // query a failed connect wrote (`utils/hubOpenFallback.ts` →
      // `?reason=…&hub=…&address=…`) travels with it, and the modal paints it.
      path: '/local',
      name: 'Local',
      redirect: (to) => ({ path: '/home', query: { ...to.query, find: '1' } }),
      meta: { requiresAuth: false, bare: true },
    },
    {
      // In-app cloud account centre — the same sections as the web account area
      // (Profile, Servers, Subscriptions, Organizations, Services, Orders,
      // Payments). The section is a path segment (`/me/subscriptions`) so links
      // and the browser back button walk it. Reached from the shell bar's
      // account button / CloudAccountPanel and the left menu; distinct from the
      // hub's own /account page.
      path: '/me/:section?',
      name: 'MyAccount',
      component: () => import('../views/accountcenter/AccountCenter.vue'),
      meta: { requiresAuth: false, cloudView: true },
    },
    {
      // App Settings — its own page (theme, desktop apps, touch panel): the
      // left menu's "App Settings" item and the ⚙ in the top bars open it.
      // Section is a path segment (`/app-settings/touch-panel`).
      path: '/app-settings/:section?',
      name: 'AppSettings',
      component: () => import('../views/Settings/AppSettingsPage.vue'),
      meta: { requiresAuth: false, bare: true },
    },
    {
      // Services (built-in app tab) — the cloud marketplace: browse businesses
      // per service, search/filter, and open one's page. Guests may browse;
      // contacting a business needs a signed-in account.
      path: '/services/:app?/:slug?',
      name: 'Services',
      component: () => import('../views/services/ServicesApp.vue'),
      meta: { requiresAuth: false, cloudView: true },
    },
    {
      // Backward compat: old Connect page redirects to the dashboard
      path: '/connect',
      redirect: '/home',
    },
    {
      // Backward compat: old Cloud page redirects to the dashboard
      path: '/cloud',
      redirect: '/home',
    },
    {
      // Backward compat: /welcome now redirects to the dashboard
      path: '/welcome',
      redirect: '/home',
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('../views/Login.vue'),
      meta: { requiresAuth: false },
    },
    {
      // "This hub has no devices bound yet" — the hub's own page, and anything
      // pointing at an unclaimed hub, lands here instead of on a login form
      // (PLAN.md §4 Phase 3: devices claim a hub, passwords do not).
      path: '/join',
      name: 'HubJoin',
      component: () => import('../views/Login.vue'),
      // Same bare layout as /login (App.vue's `useBareLayout`): this is a
      // pre-session page and must not render the hub chrome behind the card.
      meta: { requiresAuth: false, hubUnbound: true, bare: true },
    },
    {
      path: '/auth/callback',
      name: 'OAuthCallback',
      component: () => import('../views/OAuthCallback.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/register',
      name: 'Register',
      // No public sign-up: a fresh hub lands on /login, where the ways in are
      // the cloud account and (once set) a local password.
      redirect: '/login',
      meta: { requiresAuth: false },
    },
    {
      // No password-reset routes: the hub deleted `POST /api/auth/forgot-password`
      // and `POST /api/auth/reset-password` (PLAN.md §4 phase 4, hub commit
      // 51677c7), so `/forgot-password` and `/reset-password` are gone with the
      // views behind them. A user gets in with their cloud account, a local
      // password the hub checks itself, or an installer grant (F7/F10).
      path: '/',
      name: 'Hubs',
      // Electron → the launcher; local mode (dev or server, and a hub that
      // serves this UI itself) → login page; App/Web modes → servers page.
      redirect: () => {
        if (isElectron()) return '/home';
        // The RESOLVED flavor, not the raw hostname (card `t_6d25f68a`): a hub
        // serving this UI to the browser on a public hostname resolves local
        // (`config/runtime.ts` → `isHubServedOrigin`), and its visitors belong
        // on the hub's own page — the hostname test alone sent the demo house's
        // guests into the cloud launcher (`/home`).
        return readRuntimeFlavor() === 'local' && !isCapacitor() ? '/login' : '/home';
      },
      meta: { requiresAuth: false, serverView: true },
    },
    // Cloud-mode hub-scoped routes
    {
      path: '/h/:hubId',
      // A hub's root is the system dashboard now: `/layouts` means "the canvas I
      // had open", which is a different question.
      redirect: (to) => ({ path: `/h/${to.params.hubId as string}/dashboard` }),
      meta: { requiresAuth: true },
    },
    {
      // The scoped pair, at the same two paths as the unscoped ones.
      path: '/h/:hubId/dashboard',
      name: 'SystemDashboardScoped',
      component: () => import('../views/SystemDashboardPage.vue'),
      meta: { requiresAuth: true, systemPage: 'dashboard' },
    },
    {
      path: '/h/:hubId/model',
      name: 'SystemModelScoped',
      component: () => import('../views/DashboardNew.vue'),
      meta: { requiresAuth: true, systemPage: 'model' },
    },
    // LAYOUTS are the pages now (`t_layouts`). `/dashboards…` keeps working as a
    // redirect so a bookmark, a QR code or a notification link still lands.
    {
      path: '/h/:hubId/layouts',
      redirect: (to) => {
        // The guard picks the last opened layout, else the hub's first.
        return { name: 'DashboardNewScoped', params: { hubId: to.params.hubId, id: '_redirect' } };
      },
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/layouts/:id',
      name: 'DashboardNewScoped',
      component: () => import('../views/DashboardNew.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/dashboards',
      redirect: (to) => ({ path: `/h/${to.params.hubId as string}/layouts` }),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/dashboards/:id',
      redirect: (to) => ({
        path: `/h/${to.params.hubId as string}/layouts/${to.params.id as string}`,
      }),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/devices',
      name: 'DevicesScoped',
      component: () => import('../views/Devices.vue'),
      meta: { requiresAuth: true },
    },
    {
      /**
       * A SHARED project, opened by a link and visible to no one in particular.
       *
       * `requiresAuth: false` on purpose: the id in the URL is the capability, and
       * the cloud's public read decides whether it opens. `cloudView: true` keeps the
       * guard from hub-scoping it — it is neither a hub page nor an account page.
       *
       * READ-ONLY and separate from `/plans/:planId/*`: a link must never reach the
       * project shell, which carries the write actions.
       */
      path: '/public/:planId',
      name: 'PlanPublic',
      component: () => import('../views/plan/PlanPublicView.vue'),
      meta: { requiresAuth: false, cloudView: true },
    },
    {
      /**
       * Torik Plan — the cloud's KNX planning projects.
       *
       * NOT hub-scoped, deliberately: a project is created in the cloud and lives
       * there BEFORE any hub exists (owner, 2026-10-30: *"no hub at this point!
       * it's plan without a hub.. when plan is complete it will be imported to
       * hub"*). So the route stays at the top level.
       *
       * `cloudView: true` IS REQUIRED, and its absence was a real bug: the guard
       * below redirects a `requiresAuth` route that has neither a `hubId` param
       * nor this flag — in cloud/Electron mode to `/h/<activeHub>/plans` (a route
       * that does not exist) and, with no hub connected, straight back to
       * `/home`. Clicking Plan therefore appeared to do NOTHING, because it
       * bounced to the page it was clicked from. The flag says what is true: this
       * is a CLOUD page, not one of the hub's own.
       *
       * A project is a cloud HUB in shape (owner, 2026-10-30): rooms, integrations,
       * devices, settings and users, configured here before any hardware exists. So
       * `/plans/:planId` is a SHELL whose sections are children — the children
       * inherit this record's `meta`, `cloudView` included, which is what keeps the
       * guard from hub-scoping them.
       */
      path: '/plans',
      name: 'Plans',
      component: () => import('../views/plan/PlanList.vue'),
      meta: { requiresAuth: true, cloudView: true },
    },
    {
      path: '/plans/:planId',
      name: 'Plan',
      component: () => import('../views/plan/PlanShell.vue'),
      meta: { requiresAuth: true, cloudView: true },
      redirect: (to) => `/plans/${String(to.params.planId)}/overview`,
      children: [
        {
          path: 'overview',
          name: 'PlanOverview',
          component: () => import('../views/plan/PlanOverview.vue'),
        },
        {
          path: 'rooms',
          name: 'PlanRooms',
          component: () => import('../views/plan/PlanRooms.vue'),
        },
        {
          path: 'integrations/:integrationId?',
          name: 'PlanIntegrations',
          component: () => import('../views/plan/PlanIntegrations.vue'),
        },
        {
          path: 'devices',
          name: 'PlanDevices',
          component: () => import('../views/plan/PlanDevices.vue'),
        },
        {
          path: 'model',
          name: 'PlanModel',
          component: () => import('../views/plan/PlanModel.vue'),
        },
        {
          path: 'addresses',
          name: 'PlanAddresses',
          component: () => import('../views/plan/PlanAddresses.vue'),
        },
        {
          path: 'settings/:section?',
          name: 'PlanSettings',
          component: () => import('../views/plan/PlanSettings.vue'),
        },
        {
          path: 'users',
          name: 'PlanUsers',
          component: () => import('../views/plan/PlanUsers.vue'),
        },
      ],
    },
    {
      path: '/h/:hubId/rooms',
      component: () => import('../views/RoomsLayout.vue'),
      meta: { requiresAuth: true },
      redirect: (to) => `/h/${to.params.hubId as string}/rooms/_redirect`,
      children: [
        {
          path: 'overview',
          name: 'RoomsOverviewScoped',
          component: () => import('../views/Rooms.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: '_redirect',
          name: 'RoomsRedirectScoped',
          component: () => import('../views/RoomsLayout.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: ':id',
          name: 'RoomDetailScoped',
          component: () => import('../views/Room.vue'),
          meta: { requiresAuth: true },
          props: true,
        },
      ],
    },

    {
      path: '/h/:hubId/integrations/:id/datapoints',
      redirect: (to) => redirectToSettingsIntegration(to, 'datapoints'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/matter',
      redirect: (to) => redirectToSettingsIntegration(to, 'matter'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/matter-bridge',
      redirect: (to) => redirectToSettingsIntegration(to, 'matter-bridge'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/cameras',
      redirect: (to) => redirectToSettingsIntegration(to, 'cameras'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/touch',
      redirect: (to) => redirectToSettingsIntegration(to, 'touch'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/computers',
      redirect: (to) => redirectToSettingsIntegration(to, 'computers'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/touch/:panelId',
      redirect: (to) =>
        `/h/${getRouteParam(to.params.hubId)}/settings/integrations/${getRouteParam(to.params.id)}/touch/${getRouteParam(to.params.panelId)}`,
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/zwave',
      redirect: (to) => redirectToSettingsIntegration(to, 'zwave'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/zigbee',
      redirect: (to) => redirectToSettingsIntegration(to, 'zigbee'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/homeassistant',
      redirect: (to) => redirectToSettingsIntegration(to, 'homeassistant'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/torik',
      redirect: (to) => redirectToSettingsIntegration(to, 'torik'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/esphome',
      redirect: (to) => redirectToSettingsIntegration(to, 'esphome'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/wled',
      redirect: (to) => redirectToSettingsIntegration(to, 'wled'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/unifi',
      redirect: (to) => redirectToSettingsIntegration(to, 'unifi'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/hue',
      redirect: (to) => redirectToSettingsIntegration(to, 'hue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/synology',
      redirect: (to) => redirectToSettingsIntegration(to, 'synology'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/somfy',
      redirect: (to) => redirectToSettingsIntegration(to, 'somfy'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/knx',
      redirect: (to) => redirectToSettingsIntegration(to, 'knx'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/media-players',
      redirect: (to) => redirectToSettingsIntegration(to, 'media-players'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/integrations/:id/spotify',
      redirect: (to) => redirectToSettingsIntegration(to, 'spotify'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/media',
      name: 'MediaScoped',
      component: () => import('../views/Media.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/cameras',
      name: 'CamerasScoped',
      component: () => import('../views/Cameras.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/security',
      name: 'SecurityScoped',
      component: () => import('../views/SecurityTest.vue'),
      meta: { requiresAuth: true },
    },
    {
      // The columned page the rows-first one replaces, kept reachable for a
      // side-by-side look while the review is open.
      path: '/h/:hubId/security-classic',
      name: 'SecurityClassicScoped',
      component: () => import('../views/Security.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/weather',
      name: 'WeatherScoped',
      component: () => import('../views/Weather.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/intercom',
      name: 'IntercomScoped',
      component: () => import('../views/IntercomContacts.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/map',
      name: 'MapScoped',
      component: () => import('../views/Map.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/cameras/:id/live',
      name: 'CameraLiveScoped',
      component: () => import('../views/CameraLive.vue'),
      meta: { requiresAuth: true },
    },

    {
      // Redirect old /charts route to settings/charts
      path: '/h/:hubId/charts',
      redirect: (to) => `/h/${to.params.hubId as string}/settings/charts`,
    },
    {
      // The legacy users list is RETIRED (card t_5d29ff02). It rendered every
      // hub `users` row as a person — the commissioning anchor came out as
      // `Windows PC` with an invented "No email" mail line — and it was a
      // second copy of the list the settings directory already owns, so it
      // drifted from the fixed one. Redirect keeps old links and bookmarks
      // working; the guard rides the target (`/h/:hubId/settings/users` is
      // `requiresAuth`), and the hub scope is preserved exactly.
      path: '/h/:hubId/users',
      name: 'UsersScoped',
      redirect: (to) => `/h/${to.params.hubId as string}/settings/users`,
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/users/:id',
      name: 'UserDetailScoped',
      component: () => import('../views/UserDetail.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/people',
      name: 'PeopleScoped',
      component: () => import('../views/People.vue'),
      meta: { requiresAuth: true },
    },
    {
      // The hub-scoped twin of `/account`: the settings directory's Devices
      // section links here (`getHubPath('/account?tab=security')`) so the link
      // stays inside the hub scope — a non-scoped push from a scoped page is
      // what drops an Electron cloud tab back on the launcher.
      path: '/h/:hubId/account',
      name: 'AccountScoped',
      component: () => import('../views/Account.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/settings/_redirect',
      name: 'SettingsRedirect',
      component: () => import('../views/SettingsLayout.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/settings',
      component: () => import('../views/SettingsLayout.vue'),
      meta: { requiresAuth: true },
      redirect: (to) => `/h/${to.params.hubId as string}/settings/_redirect`,
      children: [
        {
          path: 'integrations',
          name: 'SettingsIntegrationsScoped',
          component: () => import('../views/Integrations.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/datapoints',
          name: 'IntegrationDatapointsScoped',
          component: () => import('../views/IntegrationDatapoints.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/matter/:tab?',
          name: 'IntegrationMatterScoped',
          component: () => import('../views/IntegrationMatter.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/matter-bridge/:tab?',
          name: 'IntegrationMatterBridgeScoped',
          component: () => import('../views/IntegrationMatterBridge.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/cameras/:tab?',
          name: 'IntegrationCamerasScoped',
          component: () => import('../views/IntegrationCameras.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/touch/:tab(panels|devices|settings|logs)?',
          name: 'IntegrationTouchPanelsScoped',
          component: () => import('../views/IntegrationTouchPanels.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/computers/:tab?',
          name: 'IntegrationComputersScoped',
          component: () => import('../views/IntegrationComputers.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/touch/:panelId',
          name: 'IntegrationTouchPanelEditorScoped',
          component: () => import('../views/Settings/TouchPanelEditor.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/zwave/:tab?',
          name: 'IntegrationZWaveScoped',
          component: () => import('../views/IntegrationZWave.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/zigbee/:tab?',
          name: 'IntegrationZigbeeScoped',
          component: () => import('../views/IntegrationZigbee.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/homeassistant/:tab?',
          name: 'IntegrationHomeAssistantScoped',
          component: () => import('../views/IntegrationHomeAssistant.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/torik/:tab?',
          name: 'IntegrationTorikScoped',
          component: () => import('../views/IntegrationTorik.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/esphome/:tab?',
          name: 'IntegrationESPHomeScoped',
          component: () => import('../views/IntegrationESPHome.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/wled/:tab?',
          name: 'IntegrationWLEDScoped',
          component: () => import('../views/IntegrationWLED.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/unifi/:tab?',
          name: 'IntegrationUnifiScoped',
          component: () => import('../views/IntegrationUnifi.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/hue/:tab?',
          name: 'IntegrationHueScoped',
          component: () => import('../views/IntegrationHue.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/synology/:tab?',
          name: 'IntegrationSynologyScoped',
          component: () => import('../views/IntegrationSynology.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/somfy/:tab?',
          name: 'IntegrationSomfyScoped',
          component: () => import('../views/IntegrationSomfy.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/knx/:tab?',
          name: 'IntegrationSettingsScoped',
          component: () => import('../views/IntegrationProtocol.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/bacnet/:tab?',
          name: 'IntegrationBACnetScoped',
          component: () => import('../views/IntegrationProtocol.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/modbus/:tab?',
          name: 'IntegrationModbusScoped',
          component: () => import('../views/IntegrationProtocol.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/mqtt/:tab?',
          name: 'IntegrationMQTTScoped',
          component: () => import('../views/IntegrationProtocol.vue'),
          meta: { requiresAuth: true },
        },
        {
          // Remote support is one page with the installer now — keep the old
          // scoped path landing on it, query and all (the KNX Remote Access tab
          // deep-links `?integrationId=`).
          path: 'remote-support',
          redirect: (to) => ({ name: 'SettingsInstallerScoped', params: to.params, query: to.query, hash: to.hash }),
        },
        {
          path: 'installer',
          name: 'SettingsInstallerScoped',
          component: () => import('../views/Settings/SettingsInstaller.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/media-players/:tab?',
          name: 'IntegrationMediaPlayersScoped',
          component: () => import('../views/IntegrationMediaPlayers.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/spotify/:tab?',
          name: 'IntegrationSpotifyScoped',
          component: () => import('../views/IntegrationSpotify.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/sonos/:tab?',
          name: 'IntegrationSonosScoped',
          component: () => import('../views/IntegrationSonos.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'rooms',
          name: 'SettingsRoomsScoped',
          component: () => import('../views/Settings/SettingsRooms.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'devices',
          name: 'SettingsDevicesScoped',
          component: () => import('../views/Settings/SettingsDevices.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'device-automations',
          name: 'SettingsDeviceAutomationsScoped',
          component: () => import('../views/Settings/SettingsDeviceAutomations.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'modes',
          name: 'SettingsModesScoped',
          component: () => import('../views/Modes.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'scenes',
          name: 'SettingsScenesScoped',
          component: () => import('../views/Scenes.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'routines',
          name: 'SettingsRoutinesScoped',
          component: () => import('../views/Routines.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'system',
          name: 'SettingsSystemScoped',
          component: () => import('../views/Settings/SettingsSystem.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'general',
          name: 'SettingsGeneralScoped',
          component: () => import('../views/Settings/SettingsGeneral.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'torik-api',
          name: 'SettingsTorikApiScoped',
          component: () => import('../views/Settings/SettingsTorikApi.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'users',
          name: 'SettingsUsersScoped',
          component: () => import('../views/Settings/SettingsUsers.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'presence',
          name: 'SettingsPresenceScoped',
          component: () => import('../views/Settings/PresenceSettings.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'agent',
          name: 'SettingsAgentScoped',
          component: () => import('../views/Settings/SettingsAgent.vue'),
          meta: { requiresAuth: true },
        },
        {
          // The demo player's page (card D2, plan §4.9): Playback now, the
          // script store's and the timeline's tabs as they land.
          path: 'demo',
          name: 'SettingsDemoScoped',
          component: () => import('../views/Settings/SettingsDemo.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'backup',
          name: 'SettingsBackupScoped',
          component: () => import('../views/Settings/SettingsBackup.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'gallery',
          name: 'SettingsGalleryScoped',
          component: () => import('../views/Settings/SettingsGallery.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'model',
          name: 'SettingsModelScoped',
          component: () => import('../views/Settings/SettingsModel.vue'),
          meta: { requiresAuth: true },
        },
        // The two pages this one replaced — "3D Scans" and "BIM" were halves of
        // the same story (owner, 2026-11-01: "why we have 2 setting pages BIM and
        // 3d scans.. We should merge into one page 3D Model"). Old links land here.
        { path: 'scans', redirect: { name: 'SettingsModelScoped' } },
        { path: 'bim', redirect: { name: 'SettingsModelScoped' } },
        {
          path: 'danger',
          name: 'SettingsDangerScoped',
          component: () => import('../views/Settings/SettingsDangerZone.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'charts',
          name: 'SettingsChartsScoped',
          component: () => import('../views/Charts.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'security',
          name: 'SettingsSecurityScoped',
          component: () => import('../views/Settings/SettingsSecurity.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'energy',
          name: 'SettingsEnergyScoped',
          component: () => import('../views/Settings/SettingsEnergy.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'weather',
          name: 'SettingsWeatherScoped',
          component: () => import('../views/Settings/SettingsWeather.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'climate',
          name: 'SettingsClimateScoped',
          component: () => import('../views/Settings/SettingsClimate.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'water',
          name: 'SettingsWaterScoped',
          component: () => import('../views/Settings/SettingsWater.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'appliances',
          name: 'SettingsAppliancesScoped',
          component: () => import('../views/Settings/SettingsAppliances.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'lights',
          name: 'SettingsLightsScoped',
          component: () => import('../views/Settings/SettingsLights.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'shades',
          name: 'SettingsShadesScoped',
          component: () => import('../views/Settings/SettingsShades.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'streaming',
          name: 'StreamingSettingsScoped',
          component: () => import('../components/CameraStreamingSettings.vue'),
          meta: { requiresAuth: true },
        },
        { path: 'media', redirect: { name: 'SettingsLibraryScoped' } },
        {
          path: 'library',
          name: 'SettingsLibraryScoped',
          component: () => import('../views/Settings/SettingsLibrary.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'logs',
          name: 'SettingsLogsScoped',
          component: () => import('../views/Settings/SettingsLogs.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'layouts',
          name: 'SettingsDashboardsScoped',
          component: () => import('../views/Settings/SettingsDashboards.vue'),
          meta: { requiresAuth: true },
        },
        { path: 'dashboards', redirect: { name: 'SettingsDashboardsScoped' } },
        { path: 'sidebar', redirect: { name: 'SettingsDashboardsScoped' } },
        {
          path: '_redirect',
          name: 'SettingsRedirectScoped',
          component: () => import('../views/SettingsLayout.vue'),
          meta: { requiresAuth: true },
        },
      ],
    },
    {
      path: '/h/:hubId/bim/:id',
      redirect: (to) => ({ name: 'DashboardNewScoped', params: { hubId: to.params.hubId, id: to.params.id } }),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/climate',
      name: 'ClimateScoped',
      component: () => import('../views/ClimateTest.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/water',
      name: 'WaterScoped',
      component: () => import('../views/Water.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/appliances',
      name: 'AppliancesScoped',
      component: () => import('../views/Appliances.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/wellness',
      name: 'WellnessScoped',
      component: () => import('../views/Wellness.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/energy',
      name: 'EnergyScoped',
      component: () => import('../views/Energy.vue'),
      meta: { requiresAuth: true },
    },
    {
      // The columned page the rows-first one replaces, kept reachable for a
      // side-by-side look while the review is open.
      path: '/h/:hubId/climate-classic',
      name: 'ClimateClassicScoped',
      component: () => import('../views/Climate.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/lights',
      name: 'LightsScoped',
      component: () => import('../views/LightsTest.vue'),
      meta: { requiresAuth: true },
    },
    {
      // The columned page the rows-first one replaces, kept reachable for a
      // side-by-side look while the review is open.
      path: '/h/:hubId/lights-classic',
      name: 'LightsClassicScoped',
      component: () => import('../views/Lights.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/shades',
      name: 'ShadesScoped',
      component: () => import('../views/ShadesTest.vue'),
      meta: { requiresAuth: true },
    },
    {
      // The columned page the rows-first one replaces, kept reachable for a
      // side-by-side look while the review is open.
      path: '/h/:hubId/shades-classic',
      name: 'ShadesClassicScoped',
      component: () => import('../views/Shades.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/maintenance',
      name: 'MaintenanceScoped',
      component: () => import('../views/Maintenance.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/agent',
      name: 'AgentScoped',
      component: () => import('../views/Agent.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/pages',
      name: 'PagesScoped',
      component: () => import('../views/Pages.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/notes',
      name: 'NotesScoped',
      component: () => import('../views/Notes.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/gallery',
      name: 'GalleryScoped',
      component: () => import('../views/Gallery.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/shopping',
      name: 'ShoppingScoped',
      component: () => import('../views/Shopping.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/calendar',
      name: 'CalendarScoped',
      component: () => import('../views/Calendar.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/alarms',
      name: 'AlarmsScoped',
      component: () => import('../views/Alarms.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/timers',
      name: 'TimersScoped',
      component: () => import('../views/Timers.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/h/:hubId/reminders',
      name: 'RemindersScoped',
      component: () => import('../views/Reminders.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/dashboard',
      /**
       * The hub's dashboard, at its own URL and composed in code (owner,
       * 2026-10-31: *"the dashboard code should be fixed.. we dont need add
       * widgets anymore. but you can reuse the widgets as components in the new
       * dash"*). It was a redirect into `/layouts/<id>`, then a layout row; now
       * it is neither a row nor a board — see `views/SystemDashboardPage.vue`.
       */
      name: 'SystemDashboard',
      component: () => import('../views/SystemDashboardPage.vue'),
      meta: { requiresAuth: true, systemPage: 'dashboard' },
    },
    {
      // The hub's 3D model — a system surface the same way the dashboard is
      // (owner: *"also make system static 3d page"*). `/home` is the connection
      // screen, so the house keeps `/model`.
      path: '/model',
      name: 'SystemModel',
      component: () => import('../views/DashboardNew.vue'),
      meta: { requiresAuth: true, systemPage: 'model' },
    },
    {
      path: '/layouts',
      redirect: () => {
        // Will be handled by beforeEach guard to redirect to last viewed or first layout
        return { name: 'DashboardNew', params: { id: '_redirect' } };
      },
      meta: { requiresAuth: true },
    },
    {
      path: '/layouts/:id',
      name: 'DashboardNew',
      component: () => import('../views/DashboardNew.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/dashboards',
      redirect: () => ({ path: '/layouts' }),
      meta: { requiresAuth: true },
    },
    {
      path: '/dashboards/:id',
      redirect: (to) => ({ path: `/layouts/${to.params.id as string}` }),
      meta: { requiresAuth: true },
    },
    {
      path: '/devices',
      name: 'Devices',
      component: () => import('../views/Devices.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/rooms',
      component: () => import('../views/RoomsLayout.vue'),
      meta: { requiresAuth: true },
      redirect: '/rooms/_redirect',
      children: [
        {
          path: 'overview',
          name: 'RoomsOverview',
          component: () => import('../views/Rooms.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: '_redirect',
          name: 'RoomsRedirect',
          component: () => import('../views/RoomsLayout.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: ':id',
          name: 'RoomDetail',
          component: () => import('../views/Room.vue'),
          meta: { requiresAuth: true },
          props: true,
        },
      ],
    },

    {
      path: '/integrations/:id/datapoints',
      redirect: (to) => redirectToSettingsIntegration(to, 'datapoints'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/matter',
      redirect: (to) => redirectToSettingsIntegration(to, 'matter'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/matter-bridge',
      redirect: (to) => redirectToSettingsIntegration(to, 'matter-bridge'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/cameras',
      redirect: (to) => redirectToSettingsIntegration(to, 'cameras'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/touch',
      redirect: (to) => redirectToSettingsIntegration(to, 'touch'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/computers',
      redirect: (to) => redirectToSettingsIntegration(to, 'computers'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/touch/:panelId',
      redirect: (to) =>
        `/settings/integrations/${getRouteParam(to.params.id)}/touch/${getRouteParam(to.params.panelId)}`,
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/zwave',
      redirect: (to) => redirectToSettingsIntegration(to, 'zwave'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/zigbee',
      redirect: (to) => redirectToSettingsIntegration(to, 'zigbee'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/homeassistant',
      redirect: (to) => redirectToSettingsIntegration(to, 'homeassistant'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/torik',
      redirect: (to) => redirectToSettingsIntegration(to, 'torik'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/esphome',
      redirect: (to) => redirectToSettingsIntegration(to, 'esphome'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/wled',
      redirect: (to) => redirectToSettingsIntegration(to, 'wled'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/unifi',
      redirect: (to) => redirectToSettingsIntegration(to, 'unifi'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/hue',
      redirect: (to) => redirectToSettingsIntegration(to, 'hue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/synology',
      redirect: (to) => redirectToSettingsIntegration(to, 'synology'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/somfy',
      redirect: (to) => redirectToSettingsIntegration(to, 'somfy'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/knx',
      redirect: (to) => redirectToSettingsIntegration(to, 'knx'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/media-players',
      redirect: (to) => redirectToSettingsIntegration(to, 'media-players'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations/:id/spotify',
      redirect: (to) => redirectToSettingsIntegration(to, 'spotify'),
      meta: { requiresAuth: true },
    },
    {
      path: '/media',
      name: 'Media',
      component: () => import('../views/Media.vue'),
      meta: { requiresAuth: true },
    },
    {
      // Player app tab (Electron shell bar) — an independent, bare app surface
      // (no hub chrome, like Panel mode): a network player the hub can cast to.
      // Mock for now — no hub data yet, so it opens without a session.
      path: '/player',
      name: 'Player',
      component: () => import('../views/PlayerMock.vue'),
      meta: { requiresAuth: false, bare: true },
    },
    {
      path: '/cameras',
      name: 'Cameras',
      component: () => import('../views/Cameras.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/security',
      name: 'Security',
      component: () => import('../views/SecurityTest.vue'),
      meta: { requiresAuth: true },
    },
    {
      // See `SecurityClassicScoped` — the old columned page, kept reachable while
      // the rows-first one is under review.
      path: '/security-classic',
      name: 'SecurityClassic',
      component: () => import('../views/Security.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/energy',
      name: 'Energy',
      component: () => import('../views/Energy.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/weather',
      name: 'Weather',
      component: () => import('../views/Weather.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/intercom',
      name: 'Intercom',
      component: () => import('../views/IntercomContacts.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/map',
      name: 'Map',
      component: () => import('../views/Map.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/cameras/:id/live',
      name: 'CameraLive',
      component: () => import('../views/CameraLive.vue'),
      meta: { requiresAuth: true },
    },

    {
      // Redirect old /charts route to settings/charts
      path: '/charts',
      redirect: '/settings/charts',
    },
    {
      // Retired with its hub-scoped twin above: `/users` is the settings
      // directory's page now (`/settings/users`), never a second list.
      path: '/users',
      name: 'Users',
      redirect: '/settings/users',
      meta: { requiresAuth: true },
    },
    {
      path: '/users/:id',
      name: 'UserDetail',
      component: () => import('../views/UserDetail.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/people',
      name: 'People',
      component: () => import('../views/People.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/settings',
      component: () => import('../views/SettingsLayout.vue'),
      meta: { requiresAuth: true },
      redirect: (to) => ({ path: `/settings/_redirect${to.params.hubId ? `?hubId=${to.params.hubId}` : ''}` }),
      children: [
        {
          path: 'integrations',
          name: 'SettingsIntegrations',
          component: () => import('../views/Integrations.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/datapoints',
          name: 'IntegrationDatapoints',
          component: () => import('../views/IntegrationDatapoints.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/matter/:tab?',
          name: 'IntegrationMatter',
          component: () => import('../views/IntegrationMatter.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/matter-bridge/:tab?',
          name: 'IntegrationMatterBridge',
          component: () => import('../views/IntegrationMatterBridge.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/cameras/:tab?',
          name: 'IntegrationCameras',
          component: () => import('../views/IntegrationCameras.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/touch/:tab(panels|devices|settings|logs)?',
          name: 'IntegrationTouchPanels',
          component: () => import('../views/IntegrationTouchPanels.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/computers/:tab?',
          name: 'IntegrationComputers',
          component: () => import('../views/IntegrationComputers.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/touch/:panelId',
          name: 'IntegrationTouchPanelEditor',
          component: () => import('../views/Settings/TouchPanelEditor.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/zwave/:tab?',
          name: 'IntegrationZWave',
          component: () => import('../views/IntegrationZWave.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/zigbee/:tab?',
          name: 'IntegrationZigbee',
          component: () => import('../views/IntegrationZigbee.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/homeassistant/:tab?',
          name: 'IntegrationHomeAssistant',
          component: () => import('../views/IntegrationHomeAssistant.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/torik/:tab?',
          name: 'IntegrationTorik',
          component: () => import('../views/IntegrationTorik.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/esphome/:tab?',
          name: 'IntegrationESPHome',
          component: () => import('../views/IntegrationESPHome.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/wled/:tab?',
          name: 'IntegrationWLED',
          component: () => import('../views/IntegrationWLED.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/unifi/:tab?',
          name: 'IntegrationUnifi',
          component: () => import('../views/IntegrationUnifi.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/hue/:tab?',
          name: 'IntegrationHue',
          component: () => import('../views/IntegrationHue.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/synology/:tab?',
          name: 'IntegrationSynology',
          component: () => import('../views/IntegrationSynology.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/somfy/:tab?',
          name: 'IntegrationSomfy',
          component: () => import('../views/IntegrationSomfy.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/knx/:tab?',
          name: 'IntegrationSettings',
          component: () => import('../views/IntegrationProtocol.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/bacnet/:tab?',
          name: 'IntegrationBACnet',
          component: () => import('../views/IntegrationProtocol.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/modbus/:tab?',
          name: 'IntegrationModbus',
          component: () => import('../views/IntegrationProtocol.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/mqtt/:tab?',
          name: 'IntegrationMQTT',
          component: () => import('../views/IntegrationProtocol.vue'),
          meta: { requiresAuth: true },
        },
        {
          // Remote support is one page with the installer now — keep the old
          // path landing on it, query and all (the KNX Remote Access tab
          // deep-links `?integrationId=`).
          path: 'remote-support',
          redirect: (to) => ({ name: 'SettingsInstaller', params: to.params, query: to.query, hash: to.hash }),
        },
        {
          path: 'installer',
          name: 'SettingsInstaller',
          component: () => import('../views/Settings/SettingsInstaller.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/media-players/:tab?',
          name: 'IntegrationMediaPlayers',
          component: () => import('../views/IntegrationMediaPlayers.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/spotify/:tab?',
          name: 'IntegrationSpotify',
          component: () => import('../views/IntegrationSpotify.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'integrations/:id/sonos/:tab?',
          name: 'IntegrationSonos',
          component: () => import('../views/IntegrationSonos.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'rooms',
          name: 'SettingsRooms',
          component: () => import('../views/Settings/SettingsRooms.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'devices',
          name: 'SettingsDevices',
          component: () => import('../views/Settings/SettingsDevices.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'device-automations',
          name: 'SettingsDeviceAutomations',
          component: () => import('../views/Settings/SettingsDeviceAutomations.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'modes',
          name: 'SettingsModes',
          component: () => import('../views/Modes.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'scenes',
          name: 'SettingsScenes',
          component: () => import('../views/Scenes.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'routines',
          name: 'SettingsRoutines',
          component: () => import('../views/Routines.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'charts',
          name: 'SettingsCharts',
          component: () => import('../views/Charts.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'system',
          name: 'SettingsSystem',
          component: () => import('../views/Settings/SettingsSystem.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'general',
          name: 'SettingsGeneral',
          component: () => import('../views/Settings/SettingsGeneral.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'torik-api',
          name: 'SettingsTorikApi',
          component: () => import('../views/Settings/SettingsTorikApi.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'users',
          name: 'SettingsUsers',
          component: () => import('../views/Settings/SettingsUsers.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'presence',
          name: 'SettingsPresence',
          component: () => import('../views/Settings/PresenceSettings.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'agent',
          name: 'SettingsAgent',
          component: () => import('../views/Settings/SettingsAgent.vue'),
          meta: { requiresAuth: true },
        },
        {
          // The hub-less twin of `SettingsDemoScoped` (card D2, plan §4.9).
          path: 'demo',
          name: 'SettingsDemo',
          component: () => import('../views/Settings/SettingsDemo.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'backup',
          name: 'SettingsBackup',
          component: () => import('../views/Settings/SettingsBackup.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'gallery',
          name: 'SettingsGallery',
          component: () => import('../views/Settings/SettingsGallery.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'model',
          name: 'SettingsModel',
          component: () => import('../views/Settings/SettingsModel.vue'),
          meta: { requiresAuth: true },
        },
        // The two pages this one replaced — see the scoped shell for the reason.
        { path: 'scans', redirect: { name: 'SettingsModel' } },
        { path: 'bim', redirect: { name: 'SettingsModel' } },
        {
          path: 'danger',
          name: 'SettingsDanger',
          component: () => import('../views/Settings/SettingsDangerZone.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'security',
          name: 'SettingsSecurity',
          component: () => import('../views/Settings/SettingsSecurity.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'energy',
          name: 'SettingsEnergy',
          component: () => import('../views/Settings/SettingsEnergy.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'weather',
          name: 'SettingsWeather',
          component: () => import('../views/Settings/SettingsWeather.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'climate',
          name: 'SettingsClimate',
          component: () => import('../views/Settings/SettingsClimate.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'water',
          name: 'SettingsWater',
          component: () => import('../views/Settings/SettingsWater.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'appliances',
          name: 'SettingsAppliances',
          component: () => import('../views/Settings/SettingsAppliances.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'lights',
          name: 'SettingsLights',
          component: () => import('../views/Settings/SettingsLights.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'shades',
          name: 'SettingsShades',
          component: () => import('../views/Settings/SettingsShades.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'streaming',
          name: 'StreamingSettings',
          component: () => import('../components/CameraStreamingSettings.vue'),
          meta: { requiresAuth: true },
        },
        { path: 'media', redirect: { name: 'SettingsLibrary' } },
        {
          path: 'library',
          name: 'SettingsLibrary',
          component: () => import('../views/Settings/SettingsLibrary.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'logs',
          name: 'SettingsLogs',
          component: () => import('../views/Settings/SettingsLogs.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'layouts',
          name: 'SettingsDashboards',
          component: () => import('../views/Settings/SettingsDashboards.vue'),
          meta: { requiresAuth: true },
        },
        { path: 'dashboards', redirect: { name: 'SettingsDashboards' } },
        { path: 'sidebar', redirect: { name: 'SettingsDashboards' } },
        {
          path: '_redirect',
          name: 'SettingsRedirectLocal',
          component: () => import('../views/SettingsLayout.vue'),
          meta: { requiresAuth: true },
        },
      ],
    },
    {
      path: '/bim/:id',
      redirect: (to) => ({ name: 'DashboardNew', params: { id: to.params.id } }),
      meta: { requiresAuth: true },
    },
    {
      path: '/climate',
      name: 'Climate',
      component: () => import('../views/ClimateTest.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/water',
      name: 'Water',
      component: () => import('../views/Water.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/appliances',
      name: 'Appliances',
      component: () => import('../views/Appliances.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/wellness',
      name: 'Wellness',
      component: () => import('../views/Wellness.vue'),
      meta: { requiresAuth: true },
    },
    {
      // See `ClimateClassicScoped` — the old columned page, kept reachable while
      // the rows-first one is under review.
      path: '/climate-classic',
      name: 'ClimateClassic',
      component: () => import('../views/Climate.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/lights',
      name: 'Lights',
      component: () => import('../views/LightsTest.vue'),
      meta: { requiresAuth: true },
    },
    {
      // See `LightsClassicScoped` — the old columned page, kept reachable while
      // the rows-first one is under review.
      path: '/lights-classic',
      name: 'LightsClassic',
      component: () => import('../views/Lights.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/shades',
      name: 'Shades',
      component: () => import('../views/ShadesTest.vue'),
      meta: { requiresAuth: true },
    },
    {
      // See `ShadesClassicScoped` — the old columned page, kept reachable while
      // the rows-first one is under review.
      path: '/shades-classic',
      name: 'ShadesClassic',
      component: () => import('../views/Shades.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/maintenance',
      name: 'Maintenance',
      component: () => import('../views/Maintenance.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/agent',
      name: 'Agent',
      component: () => import('../views/Agent.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/pages',
      name: 'Pages',
      component: () => import('../views/Pages.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/notes',
      name: 'Notes',
      component: () => import('../views/Notes.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/gallery',
      name: 'Gallery',
      component: () => import('../views/Gallery.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/shopping',
      name: 'Shopping',
      component: () => import('../views/Shopping.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/calendar',
      name: 'Calendar',
      component: () => import('../views/Calendar.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/alarms',
      name: 'Alarms',
      component: () => import('../views/Alarms.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/timers',
      name: 'Timers',
      component: () => import('../views/Timers.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/reminders',
      name: 'Reminders',
      component: () => import('../views/Reminders.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/notifications',
      name: 'Notifications',
      component: () => import('../views/Notifications.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/account',
      name: 'Account',
      component: () => import('../views/Account.vue'),
      meta: { requiresAuth: true },
    },

    // ── Panel mode (kiosk layout) ─────────────────────────────────────
    {
      path: '/panel',
      component: () => import('../layouts/PanelLayout.vue'),
      meta: { requiresAuth: true, panelMode: true },
      children: [
        {
          path: ':pageId',
          name: 'PanelPage',
          component: () => import('../components/panel/PanelPageRouter.vue'),
          meta: { requiresAuth: true, panelMode: true },
        },
      ],
    },

    // ── Panel miniapp: a panel surface BESIDE the app ──────────────────
    // Not `panelMode`: this route does NOT take the window over. It renders the
    // panel surface as an overlay inside the normal app, with its own panel
    // credential held in memory (`stores/panelApp.ts`) while the app keeps
    // whatever session it has — the parallel half of POSITIONING.md's
    // Consequence 4. No `requiresAuth`: a device with no user account at all
    // (a tablet configured as a panel for a child) must be able to open it.
    {
      path: '/miniapp/panel/:pageId?',
      name: 'PanelMiniApp',
      component: () => import('../views/PanelMiniApp.vue'),
      meta: { panelApp: true },
    },
  ],
});

// ── Track the last position, PER CONNECTION ──────────────────────────────
// One rule for every position the app remembers (`utils/hubPositions.ts`):
// write ONLY the keys of the connection being VISITED. The old code walked
// `getHubSessionKeys(to.params.hubId)`, which deliberately also yields
// `otto_active_hub_key` — the hub being LEFT — so opening a room on hub B
// stored B's room id under hub A's key, and coming back to A the reader
// (correctly) read A's own key and got a room that only exists on B. That is
// the owner's report: "you go to a different hub and it tries to open a room
// which exists only on previously connected home". Credential readers still
// want the permissive list; a position must not have it.
router.afterEach((to) => {
  const connection = visitedConnection(to.params.hubId);

  // If visiting a settings sub-page, store it (per hub)
  if (to.path.includes('/settings/') && !to.path.includes('/settings/_redirect')) {
    // Extract the settings page name (e.g., 'matter-bridge' from '/settings/matter-bridge')
    const match = to.path.match(/\/settings\/([^/]+)/);
    if (match && match[1]) {
      writeHubPosition(LAST_VIEWED_SETTINGS_PAGE, match[1], connection);
    }
  }

  // Track last visited rooms page (per hub). No bare, hub-less
  // `lastViewedRoomsPage` write any more: every reader walks a hub-scoped key
  // list (which is the whole point of this card), so the hub-less key had no
  // reader at all — it was written for nothing and was exactly the shape of
  // value the owner's report is made of.
  if (to.path.includes('/rooms/') && !to.path.includes('/rooms/_redirect')) {
    // Extract the rooms sub-path (e.g., 'overview' or a room ID)
    const roomMatch = to.path.match(/\/rooms\/([^/]+)/);
    if (roomMatch && roomMatch[1]) {
      writeHubPosition(LAST_VIEWED_ROOMS_PAGE, roomMatch[1], connection);
    }
  }

  // Track last visited hub path across ALL sections (used on reconnect to restore position)
  const requiresAuth = to.matched.some((r) => r.meta.requiresAuth);
  const isTransient =
    to.path.includes('_redirect') ||
    to.path.startsWith('/servers') ||
    to.path.startsWith('/login') ||
    to.path.startsWith('/join') ||
    to.path.startsWith('/register') ||
    to.path.startsWith('/auth/') ||
    to.path.startsWith('/panel') ||
    to.path.startsWith('/miniapp') ||
    to.path.startsWith('/pair') ||
    to.path.startsWith('/play');
  if (requiresAuth && !isTransient) {
    let segment = to.params.hubId ? to.path.replace(/^\/h\/[^/]+\//, '') : to.path.replace(/^\//, '');
    // Dashboard IDs can disappear; store the sentinel so _redirect re-validates
    if (/^dashboards\/[^/]+$/.test(segment)) segment = 'dashboards/_redirect';
    if (segment) {
      writeHubPosition(LAST_HUB_PATH, segment, connection);
    }
  }
});

// Navigation history for smart back button
const navigationHistory: string[] = [];

// Navigation guard
router.beforeEach(async (to, from) => {
  const authStore = useAuthStore();
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const isCloudView = to.matched.some((record) => record.meta.cloudView);
  const isElectronOnly = to.matched.some((record) => record.meta.electronOnly);
  const isPanelMode = to.matched.some((record) => record.meta.panelMode);
  const isPlayMode = to.matched.some((record) => record.meta.playMode);
  const isRunningInElectron = isElectron();
  const isHubScopedRoute = typeof to.params.hubId !== 'undefined';
  // A cloud session IS a signed-in state, even though it carries no LOCAL hub
  // token (`authStore.isAuthenticated` only ever reflects the hub's own JWT).
  // The hub is reached over the WebRTC channel with the cloud token, so hub
  // routes are legitimately open. Electron has had this exemption; Capacitor
  // did not — and on the tablet every hub route (tapping "Open App" on a
  // cloud-connected hub) was bounced straight back to /home with no request
  // and no error, which reads as "the button does nothing".
  const hasCloudAccess = authStore.isCloudAuthenticated && (isHubScopedRoute || isCloudView);
  const hasElectronCloudAccess = isRunningInElectron && hasCloudAccess;

  // One home everywhere: the dashboard (`/home`) is the landing surface for
  // every platform — browser, Capacitor, cloud-embedded UI and Electron.

  const isPanelPairing = to.matched.some((record) => record.meta.panelPairing);
  const isPanelApp = to.matched.some((record) => record.meta.panelApp);
  // What this device IS, from the owner's configuration (`config/panelDevice.ts`).
  // The old predicate here was `isTouchDevice()` — "running Capacitor and has a
  // panel token somewhere" — which is a statement about hardware and made "any
  // app can be configured as a panel" (POSITIONING.md, Consequence 4) untrue.
  const isPanelDevice = isPanelDeviceTakeover();

  // Touch device guard: on devices configured as panels, bypass normal auth flow
  if (isCapacitor() && isPanelDevice) {
    // Allow the /pair, /play-pair and panel/play routes to pass through freely.
    if (isPanelPairing || isPanelMode || isPlayMode) {
      return;
    }

    try {
      const { useTouchPanelStore } = await import('../stores/touchPanel');
      const touchPanelStore = useTouchPanelStore();

      // Restore persisted credentials on first navigation after app restart
      if (!touchPanelStore.initialized) {
        await touchPanelStore.restoreState();
      }

      if (!touchPanelStore.isPaired) {
        // Not yet paired — send to appropriate pairing flow
        const dest = to.path === '/play-pair' ? '/play-pair' : '/pair';
        if (to.path !== dest) {
          return dest;
        }
      } else if (touchPanelStore.isPlayMode) {
        if (!to.path.startsWith('/play')) {
          return '/play';
        }
      } else {
        // Already paired as touch panel — only enforce /panel while panel mode is active.
        // If isPanelMode is false (admin exited via PIN), allow normal navigation.
        if (touchPanelStore.isPanelMode && !to.path.startsWith('/panel')) {
          return '/panel';
        }
      }
    } catch {
      return '/pair';
    }
  }

  // Panel/Play mode guard: when kiosk mode is active, redirect all non-kiosk routes.
  // Only enforce while the device is actually paired — otherwise a stale
  // `touch_panel_mode=true` (e.g. after an interrupted unpair) would bounce
  // /login ↔ /panel/devices in an infinite redirect loop.
  if (!isPanelMode && !isPlayMode) {
    try {
      const { useTouchPanelStore } = await import('../stores/touchPanel');
      const touchPanelStore = useTouchPanelStore();
      if (touchPanelStore.isPanelMode && touchPanelStore.isPaired) {
        if (!to.path.startsWith('/panel')) {
          return '/panel/devices';
        }
      } else if (touchPanelStore.isPlayMode) {
        if (!to.path.startsWith('/play')) {
          return '/play';
        }
      }
    } catch {
      // Store not ready yet, continue
    }
  }

  // Track navigation history (exclude dashboard routes for smart back)
  if (from.name && !String(from.path).includes('/dashboards/')) {
    navigationHistory.push(from.fullPath);
    // Keep only last 10 entries
    if (navigationHistory.length > 10) {
      navigationHistory.shift();
    }
  }

  const isRunningInCapacitor = isCapacitor();

  // Capacitor (Android/iOS): unauth → servers page
  // (Touch devices are handled by the touch-device guard above and skip this block)
  // A cloud session passes through: hub routes are served by the hub over the
  // WebRTC channel and carry no local JWT.
  if (
    isCapacitor() &&
    !isPanelDevice &&
    requiresAuth &&
    !authStore.isAuthenticated &&
    !hasCloudAccess &&
    to.path !== '/home'
  ) {
    return '/home';
  }

  // Electron: unauth → launcher (the pinned home tab)
  // (Panel/Play kiosk routes are exempt — the pairing guard below lets paired
  // panel/play devices through on their device token. Without this exemption,
  // a freshly paired panel bounces /panel → /servers → /panel/devices →
  // /servers ... in an infinite redirect loop and appears to hang on the
  // 'Connecting to your panel…' screen.)
  if (
    isRunningInElectron &&
    (to.path === '/' || requiresAuth) &&
    !authStore.isAuthenticated &&
    !hasElectronCloudAccess &&
    !isPanelMode &&
    !isPlayMode
  ) {
    return '/home';
  }

  // Plain browser mode (not Electron, not Capacitor, not embedded): unauth → login
  // (Pairing + panel/play kiosk routes are exempt — they either run
  // unauthenticated (pairing) or are gated on the device token below.
  // `/home` reaches this rule too unless the resolved flavor is local — a hub
  // that serves this UI itself is local however its hostname reads (card
  // `t_6d25f68a`), so its visitors get the hub's sign-in page, not the launcher.)
  if (
    !isRunningInElectron &&
    !isRunningInCapacitor &&
    !isPanelDevice &&
    !authStore.isAuthenticated &&
    !authStore.isCloudAuthenticated &&
    to.path !== '/login' &&
    to.path !== '/join' &&
    to.path !== '/register' &&
    to.path !== '/auth/callback' &&
    (to.path !== '/home' || readRuntimeFlavor() === 'local') &&
    !to.path.startsWith('/auth/') &&
    !isPanelPairing &&
    !isPanelMode &&
    !isPlayMode &&
    !isPanelApp
  ) {
    return '/login';
  }

  // Web mode (embedded in otto-web): cloud auth required
  // Import dynamic to avoid circular dependency at module level
  const { isOttoWebEmbedded } = await import('../config/runtime');
  if (isOttoWebEmbedded()) {
    if (requiresAuth && !authStore.isCloudAuthenticated && to.path !== '/login' && !to.path.startsWith('/auth/')) {
      return '/login';
    }
    // Redirect root to the dashboard when authenticated
    if (to.path === '/' && authStore.isCloudAuthenticated) {
      return '/home';
    }
    // Redirect from login to the dashboard if already authenticated
    if (to.path === '/login' && authStore.isCloudAuthenticated) {
      return '/home';
    }
  }

  // Redirect non-Electron users away from Electron-only routes
  if (isElectronOnly && !isRunningInElectron) {
    return '/login';
  }

  // Handle settings redirect requests
  if (to.path.includes('/settings/_redirect')) {
    // This connection's own keys only (`utils/hubPositions.ts`) — the same rule
    // the writer above uses, so a settings page opened on one hub can never be
    // restored on another. The hub-less legacy key is gone with it: nothing has
    // written one since positions became per-hub, and a position with no hub
    // identity belongs to no connection.
    const lastSettingsPage =
      readHubPosition(LAST_VIEWED_SETTINGS_PAGE, visitedConnection(to.params.hubId)) || 'integrations';
    const normalizedSettingsPage =
      lastSettingsPage === 'sidebar'
        ? 'dashboards'
        : lastSettingsPage === 'matter'
          ? 'matter-bridge'
          : lastSettingsPage === 'remote-support'
            ? 'installer'
            : lastSettingsPage;

    // A stale/removed settings slug stored under lastViewedSettingsPage:* would
    // otherwise redirect to a route with no matching child and render an empty
    // settings page. Validate against the known slugs and fall back to the
    // default page instead.
    const VALID_SETTINGS_PAGES = new Set([
      'integrations',
      'installer',
      'rooms',
      'devices',
      'device-automations',
      'scenes',
      'routines',
      'system',
      'general',
      'torik-api',
      'users',
      'presence',
      'agent',
      'demo',
      'backup',
      'gallery',
      'bim',
      'danger',
      'charts',
      'security',
      'energy',
      'weather',
      'climate',
      'water',
      'lights',
      'shades',
      'streaming',
      'library',
      'logs',
      'dashboards',
      'matter-bridge',
    ]);
    const safeSettingsPage = VALID_SETTINGS_PAGES.has(normalizedSettingsPage) ? normalizedSettingsPage : 'integrations';

    if (to.params.hubId) {
      return { path: `/h/${to.params.hubId as string}/settings/${safeSettingsPage}`, replace: true };
    } else {
      return { path: `/settings/${safeSettingsPage}`, replace: true };
    }
  }

  // Handle rooms redirect requests
  if (to.path.includes('/rooms/_redirect')) {
    // This connection's own keys only: `getHubSessionKeys()` also yields
    // `otto_active_hub_key` — the hub being left — so its stored room id would
    // be restored on the new hub, where that room does not exist (the room page
    // then bounces away). Room ids are likewise not portable between hubs.
    const storedRoomsPage = readHubPosition(LAST_VIEWED_ROOMS_PAGE, visitedConnection(to.params.hubId));
    // A stored id also outlives its hub: installs that ran the buggy writer (and
    // every build before this one) hold `lastViewedRoomsPage:<key>` values that
    // belong to a DIFFERENT hub. Validating the id against the hub being
    // visited is what heals those: a foreign room falls back to `overview`.
    const lastRoomsPage = await resolveRoomsPosition(storedRoomsPage, roomExistsOnVisitedHub);

    if (to.params.hubId) {
      return { path: `/h/${to.params.hubId as string}/rooms/${lastRoomsPage}`, replace: true };
    } else {
      return { path: `/rooms/${lastRoomsPage}`, replace: true };
    }
  }

  // Handle dashboard redirect requests
  if (to.params.id === '_redirect' || to.params.id === '_redirect-home') {
    let canUseLocalAuth = authStore.isAuthenticated;
    if (!isCloud() && !hasElectronCloudAccess && authStore.isAuthenticated && !authStore.user) {
      canUseLocalAuth = await authStore.checkAuth();
    }

    const canProceed = isCloud() || hasCloudAccess ? authStore.isCloudAuthenticated : canUseLocalAuth;

    if (!canProceed) {
      return isRunningInElectron || isRunningInCapacitor ? '/home' : '/login';
    }

    const { useDashboardStore } = await import('../stores/dashboard');
    const { apiFetch } = await import('../services/api');
    const dashboardStore = useDashboardStore();

    // Load dashboards if not already loaded. The hub the route names is passed
    // on so the store reads the remembered dashboard for THIS connection (it
    // reads the same key list this reader does, `utils/hubPositions.ts`).
    if (dashboardStore.dashboards.length === 0) {
      await dashboardStore.loadDashboards(to.params.hubId);
    }

    // Get last viewed dashboard from localStorage (per hub): this connection's
    // own keys only — the permissive `getHubSessionKeys()` also yields the hub
    // being LEFT, which is how another hub's dashboard id used to be picked up
    // (the profile/route equivalent of the rooms bug this card fixes). The id is
    // validated against THIS hub's dashboards just below, so a stale foreign id
    // falls back to the hub's own dashboards instead of an empty page.
    // `stores/dashboard.ts` writes and reads this position by the same rule
    // since card t_251ff2eb, so the value the store just picked is one of the
    // same keys this reader walks.
    const lastViewedId = readHubPosition(LAST_VIEWED_DASHBOARD, visitedConnection(to.params.hubId));
    let targetDashboardId: string | null = null;
    const candidateIds: string[] = [];

    const addCandidate = (id: string | null | undefined) => {
      const normalized = String(id ?? '').trim();
      if (!normalized) return;
      if (!candidateIds.includes(normalized)) candidateIds.push(normalized);
    };

    // A phone opens the page marked as the mobile default (`mobileDefault`): one
    // column of cards reads at arm's length, where the same widgets spread over
    // four columns are four cards squeezed into 375px. It only leads the list —
    // an empty mobile page still falls through to the page the hub would have
    // opened anyway.
    if (
      typeof window !== 'undefined' &&
      resolveNarrowSurface({
        width: window.innerWidth,
        height: window.innerHeight,
        portrait: window.innerHeight >= window.innerWidth,
      })
    ) {
      addCandidate(pickMobileDefault(dashboardStore.dashboards, isLayoutDashboard)?.id);
    }

    /**
     * Where `/layouts` opens: the row this connection had open, else a page, else
     * a canvas. A SYSTEM page is never a candidate — the dashboard and the 3D
     * house answer at their own paths (`/dashboard`, `/model`), and this list is
     * the answer to "where was I", not "which of the app's surfaces is this".
     */
    const openable = dashboardStore.dashboards.filter((row) => !isSystemPageRow(row));
    const pages = openable.filter((row) => !isLayoutDashboard(row));
    const canvases = openable.filter((row) => isLayoutDashboard(row));

    if (lastViewedId && openable.some((row) => row.id === lastViewedId)) {
      addCandidate(lastViewedId);
    }
    pages.forEach((dashboard) => addCandidate(dashboard.id));
    canvases.forEach((dashboard) => addCandidate(dashboard.id));

    // Prefer the first dashboard that actually contains widgets. This avoids
    // landing on empty placeholder dashboards after local hub login.
    if (!targetDashboardId) {
      for (const candidateId of candidateIds) {
        try {
          const detail = await apiFetch(`/layouts/${candidateId}`);
          const widgets = Array.isArray(detail?.widgets) ? detail.widgets : [];
          if (widgets.length > 0) {
            targetDashboardId = candidateId;
            break;
          }
        } catch {
          // Ignore per-dashboard fetch failures here and try the next candidate.
        }
      }
    }

    // If every dashboard is empty, fall back to the first available one.
    if (!targetDashboardId && candidateIds.length > 0) {
      targetDashboardId = candidateIds[0];
    }

    if (targetDashboardId) {
      if (to.params.hubId) {
        return { name: 'DashboardNewScoped', params: { hubId: to.params.hubId, id: targetDashboardId }, replace: true };
      } else {
        return { name: 'DashboardNew', params: { id: targetDashboardId }, replace: true };
      }
    } else {
      // Nothing to reopen: the dashboard is everyone's page and needs no row
      // (it used to be the seeded board, then the rooms overview).
      return {
        path: to.params.hubId ? `/h/${to.params.hubId as string}/dashboard` : '/dashboard',
        replace: true,
      };
    }
  }

  let validatedLocalAuth = authStore.isAuthenticated;

  // On startup, Electron/local mode may have a persisted access token from the
  // previously selected hub. Validate it before allowing protected routes,
  // otherwise stores/pages can briefly load against a hub with an invalid token
  // and render mixed or unauthorized data before logout catches up.
  if (requiresAuth && !isCloud() && !hasElectronCloudAccess && authStore.isAuthenticated && !authStore.user) {
    validatedLocalAuth = await authStore.checkAuth();
  }

  // In cloud mode, authentication is via cloudAccessToken (not local hub accessToken).
  // `hasCloudAccess` carries the same idea to Capacitor, whose runtime mode is
  // 'capacitor' (so plain `isCloud()` is false) even when the hub is reached
  // through the cloud. Without it every hub route on the tablet fell through to
  // `validatedLocalAuth`, which is false by design in a cloud session (there is
  // no local hub JWT) — and the guard bounced the navigation to /home.
  const effectivelyAuthenticated = isCloud() || hasCloudAccess ? authStore.isCloudAuthenticated : validatedLocalAuth;

  // Stale connection detection: if the user appears authenticated but the
  // socket has been disconnected for >10s (e.g. after tab was backgrounded),
  // redirect to the connect screen so the UI doesn't appear frozen.
  // Electron/Capacitor go back to the servers page (where saved hubs can be
  // silently re-logged-in); only the plain browser goes to the login page.
  if (requiresAuth && effectivelyAuthenticated && !isCloud() && !hasCloudAccess) {
    const socketStore = useSocketStore();
    if (!socketStore.connected && socketStore.lastDisconnectTime > 0) {
      const staleDuration = Date.now() - socketStore.lastDisconnectTime;
      if (staleDuration > 10_000) {
        console.warn('[Router] Socket stale for', Math.round(staleDuration / 1000), 's, redirecting to connect screen');
        authStore.logout();
        return isRunningInElectron || isRunningInCapacitor ? '/home' : '/login';
      }
    }
  }

  // Electron cloud-scoped routes must restore the remote hub WebRTC session
  // before page components start fetching /devices, /rooms, /images, etc.
  // Otherwise the renderer falls back to the local otto-local API and scoped
  // room/device pages can show wrong data or redirect on false 404s.
  // Cloud-scoped routes (Electron AND web cloud mode) must restore the remote
  // hub WebRTC session before page components start fetching /devices, /rooms,
  // /images, etc. Otherwise requests fall back to HTTP against the cloud API
  // and scoped room/device pages show wrong data or 404s.
  if (
    (isRunningInElectron || isCloud() || isRunningInCapacitor) &&
    isHubScopedRoute &&
    authStore.isCloudAuthenticated &&
    authStore.cloudAccessToken
  ) {
    const requestedHubId = getRouteParam(to.params.hubId);
    const webrtc = useWebRTCStore();

    if (!webrtc.connected || webrtc.hubId !== requestedHubId) {
      if (webrtc.hubId && webrtc.hubId !== requestedHubId) {
        webrtc.disconnect();
      }

      try {
        await webrtc.connectToHub(requestedHubId, authStore.cloudAccessToken);
      } catch (error) {
        console.error('[Router] Failed to restore cloud hub session', {
          hubId: requestedHubId,
          error,
        });
        // A restore that did not open does not fall *silently* to /home (card
        // `t_dbfe328d`): the cloud's own refusal of the tunnel is a fact with a
        // name (`services/cloudSessionRefusal.ts` → `remote_access_not_licensed`:
        // the hub is set to local access only), and the person's own way in is
        // asked once (`utils/hubOpenFallback.ts`). The facts it is asked with are
        // this device's own record of the hub the route names
        // (`services/serverProfiles.ts`: its name, and the LAN address that
        // answered through `services/localServerTable.ts` → `knownHubAddress`,
        // the same reader the launcher's search hit asks). The Local page paints
        // the refusal's sentence in the query the plan writes; a hub with no
        // address on this device's network keeps the launcher, exactly as it did
        // before.
        const rows = hydrateServerProfiles();
        const profile = profileFor({ cloudId: requestedHubId, hubId: requestedHubId }, rows);
        return resolveHubOpenPlan({
          failure:
            classifyCloudSessionRefusal(errorStatus(error) ?? null, { code: errorCode(error) }) ?? 'no-credential',
          accountSignedIn: authStore.isCloudAuthenticated,
          hubName: profile?.name ?? null,
          address: knownHubAddress({ cloudId: requestedHubId, hubId: requestedHubId }),
        }).route;
      }
    }
  }

  if (requiresAuth && !effectivelyAuthenticated) {
    // Allow paired panel/play devices to access kiosk routes without a user JWT
    if (isPanelMode || isPlayMode) {
      try {
        const { useTouchPanelStore } = await import('../stores/touchPanel');
        const touchPanelStore = useTouchPanelStore();
        if (!touchPanelStore.initialized) {
          await touchPanelStore.restoreState();
        }
        if (touchPanelStore.isPaired) {
          return;
        }
      } catch {
        /* fall through to normal redirect */
      }
    }
    // In Electron/Capacitor, redirect to servers page (Electron: launcher)
    if (isPanelPairing) {
      return;
    } else if (isRunningInElectron) {
      // A hub route this window holds no credential for does not fall *silently*
      // to /home (card `t_bfe0edc9`, the shape of the `hasCloudAccess` fix): the
      // person is on a hub they cannot open, and the Local page is where that
      // hub's ways in are — named, with the refusal explained. The table is
      // `utils/hubOpenFallback.ts`; a window on no hub at all, or on a hub that
      // is not on this network, still gets the launcher.
      const endpoint = readTabActiveHub();
      return resolveHubOpenPlan({
        failure: 'no-credential',
        accountSignedIn: authStore.isCloudAuthenticated,
        hubName: endpoint?.name ?? null,
        address: endpoint ? { ip: endpoint.ip, port: endpoint.port } : null,
      }).route;
    } else if (isRunningInCapacitor) {
      return '/home';
    } else {
      return '/login';
    }
  } else if ((to.path === '/login' || to.path === '/register') && effectivelyAuthenticated) {
    if (isLocal()) {
      return '/rooms/overview';
    } else {
      return '/home';
    }
  } else if (to.path === '/' && effectivelyAuthenticated && (isLocal() || isCapacitor())) {
    return '/rooms/overview';
  } else if (to.path === '/' && effectivelyAuthenticated && isCloud()) {
    return '/home';
  } else if (isCloud() && requiresAuth && !isCloudView && !to.params.hubId) {
    // In cloud mode, force hub-scoped URLs when connected
    const webrtc = useWebRTCStore();
    const activeHubId = webrtc.hubId;
    if (activeHubId) {
      const targetPath = `/h/${activeHubId}${to.fullPath}`;
      return targetPath;
    } else {
      // No active hub connection; send to the dashboard
      return '/home';
    }
  }
  // Allow navigation to proceed
});

// Export navigation history for smart back button
export default router;

export function getNavigationHistory() {
  return navigationHistory;
}
